import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  LinearProgress,
  Alert,
  SelectChangeEvent,
  TextField,
  CircularProgress,
} from '@mui/material';
import { CloudUpload as CloudUploadIcon } from '@mui/icons-material';
import { getUploadUrl, uploadVideoToS3, startProcessing, getInstanceTypes, getAsgStatus, type ProcessRequest, type InstanceType } from '../api/videoApi';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://f423zq3mxf.execute-api.us-east-1.amazonaws.com/prod';

interface VideoUploadComponentProps {
  onJobStarted?: (jobId: string) => void;
}

export default function VideoUploadComponent({ onJobStarted }: VideoUploadComponentProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [model, setModel] = useState<'base' | 'large' | 'small'>('base');
  const [instanceType, setInstanceType] = useState<string>('');
  const [exportExr, setExportExr] = useState(true);
  const [targetFps, setTargetFps] = useState<number | null>(null);
  const [gpuAvailable, setGpuAvailable] = useState<boolean | null>(null);
  const [containerHealthy, setContainerHealthy] = useState<boolean | null>(null);
  const [instanceTypes, setInstanceTypes] = useState<InstanceType[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load instance types on mount
  useEffect(() => {
    getInstanceTypes()
      .then((types) => {
        setInstanceTypes(types);
        if (types.length > 0 && !instanceType) {
          setInstanceType(types[0].name);
        }
      })
      .catch((err) => {
        console.error('Failed to load instance types:', err);
        setError('Failed to load GPU instance types');
      });
  }, [instanceType]);

  // Poll GPU ASG availability so the warning clears when instances start
  useEffect(() => {
    const checkGpu = () => {
      getAsgStatus()
        .then((status) => {
          setGpuAvailable(status.current_instances > 0);
        })
        .catch((err) => {
          console.error('Failed to check GPU availability:', err);
        });
    };

    checkGpu();
    const interval = setInterval(checkGpu, 10_000);
    return () => clearInterval(interval);
  }, []);

  // Poll container health when GPU is available but container isn't confirmed healthy
  useEffect(() => {
    if (!gpuAvailable) {
      setContainerHealthy(null);
      return;
    }

    // Already healthy — no need to poll
    if (containerHealthy) return;

    const checkHealth = async () => {
      try {
        const resp = await fetch(`${API_BASE_URL}/api/video/status/healthcheck-probe`);
        // 502/503 = ALB has no healthy targets; anything else means container is up
        setContainerHealthy(resp.status !== 502 && resp.status !== 503);
      } catch {
        setContainerHealthy(false);
      }
    };

    checkHealth();
    const interval = setInterval(checkHealth, 10_000);
    return () => clearInterval(interval);
  }, [gpuAvailable, containerHealthy]);

  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('video/')) {
        setError('Please select a video file');
        return;
      }
      setSelectedFile(file);
      setError(null);
    }
  }, []);

  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const handleDrop = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();

    const file = event.dataTransfer.files?.[0];
    if (file && file.type.startsWith('video/')) {
      setSelectedFile(file);
      setError(null);
    } else {
      setError('Please drop a video file');
    }
  }, []);

  const handleUpload = useCallback(async () => {
    if (!selectedFile) {
      setError('Please select a video file');
      return;
    }

    if (!instanceType) {
      setError('Please select a GPU instance type');
      return;
    }

    // Re-check GPU availability
    try {
      const asgStatus = await getAsgStatus();
      const available = asgStatus.current_instances > 0;
      setGpuAvailable(available);
      if (!available) {
        setError('No GPU nodes are currently running. Please start GPU instances from the GPU Dashboard before submitting a job.');
        return;
      }
    } catch {
      // If the check fails, allow the upload to proceed
    }

    setUploading(true);
    setError(null);
    setUploadProgress(0);

    try {
      const urlResult = await getUploadUrl(selectedFile.name);
      setUploadProgress(30);

      await uploadVideoToS3(selectedFile, urlResult.upload_url);
      setUploadProgress(100);

      setProcessing(true);
      const processRequest: ProcessRequest = {
        video_s3_key: urlResult.video_s3_key,
        model,
        instance_type: instanceType,
        export_exr: exportExr,
        target_fps: targetFps,
      };

      const processResult = await startProcessing(processRequest);

      if (onJobStarted) {
        onJobStarted(processResult.job_id);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload/processing failed');
    } finally {
      setUploading(false);
      setProcessing(false);
    }
  }, [selectedFile, model, instanceType, exportExr, targetFps, onJobStarted]);

  const uploadDisabled = !selectedFile || !instanceType || uploading || processing || !containerHealthy;

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        Upload Video for Processing
      </Typography>

      {gpuAvailable === false && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          No GPU nodes are currently running. Start instances from the GPU Dashboard to process videos.
        </Alert>
      )}

      {gpuAvailable && !containerHealthy && (
        <Alert severity="info" sx={{ mb: 2 }} icon={<CircularProgress size={20} />}>
          GPU instance is running. Waiting for the processing container to start (this can take several minutes on first launch while the Docker image is pulled)...
        </Alert>
      )}

      {gpuAvailable && containerHealthy && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Backend is ready. You can upload a video for processing.
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* File Upload Area */}
      <Box
        sx={{
          border: '2px dashed',
          borderColor: 'primary.main',
          borderRadius: 2,
          p: 4,
          textAlign: 'center',
          mb: 3,
          cursor: 'pointer',
          '&:hover': {
            borderColor: 'primary.dark',
            backgroundColor: 'action.hover',
          },
        }}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={() => document.getElementById('video-file-input')?.click()}
      >
        <input
          id="video-file-input"
          type="file"
          accept="video/*"
          style={{ display: 'none' }}
          onChange={handleFileSelect}
        />
        <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
        <Typography variant="body1" gutterBottom>
          {selectedFile ? selectedFile.name : 'Drag and drop a video file here, or click to select'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Supported formats: MP4, AVI, MOV, etc.
        </Typography>
      </Box>

      {uploading && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress variant="determinate" value={uploadProgress} />
          <Typography variant="caption" color="text.secondary">
            {uploadProgress < 100 ? 'Uploading...' : 'Upload complete, starting processing...'}
          </Typography>
        </Box>
      )}

      {/* Model Selection */}
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Depth Anything 3 Model</InputLabel>
        <Select value={model} label="Depth Anything 3 Model" onChange={(e) => setModel(e.target.value as 'base' | 'large' | 'small')}>
          <MenuItem value="base">Base</MenuItem>
          <MenuItem value="large">Large</MenuItem>
          <MenuItem value="small">Small</MenuItem>
        </Select>
      </FormControl>

      {/* GPU Instance Type Selection */}
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>GPU Instance Type</InputLabel>
        <Select value={instanceType} label="GPU Instance Type" onChange={(e: SelectChangeEvent) => setInstanceType(e.target.value)}>
          {instanceTypes.map((type) => (
            <MenuItem key={type.name} value={type.name}>
              {type.name} - {type.gpu_type} ({type.gpu_count} GPU, {type.memory_gb}GB RAM
              {type.estimated_cost_per_hour && `, ~$${type.estimated_cost_per_hour}/hr`})
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Export Options */}
      <Box sx={{ mb: 2 }}>
        <FormControlLabel
          control={<Checkbox checked={exportExr} onChange={(e) => setExportExr(e.target.checked)} />}
          label="Export EXR depth maps"
        />
      </Box>

      {/* Target FPS */}
      <TextField
        fullWidth
        type="number"
        label="Target FPS (optional)"
        helperText="Leave empty to extract all frames at native FPS"
        value={targetFps ?? ''}
        onChange={(e) => {
          const val = e.target.value;
          setTargetFps(val === '' ? null : parseFloat(val));
        }}
        inputProps={{ min: 0.1, step: 0.1 }}
        sx={{ mb: 2 }}
      />

      {/* Upload Button */}
      <Button
        variant="contained"
        fullWidth
        onClick={handleUpload}
        disabled={uploadDisabled}
        size="large"
      >
        {uploading || processing ? 'Processing...' : 'Upload and Process'}
      </Button>
    </Paper>
  );
}
